<script setup lang="ts">
import OzBadge, { OzBadgeColorScheme } from '@@/library/v4/components/OzBadge.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzLoadingSpinner from '@@/library/v4/components/OzLoadingSpinner.vue'
import type { PlansState, UpgradeSource } from '@@/pinia/personal_plans_store'
import { PaymentSchedule, Tier } from '@@/pinia/personal_plans_store'
import { computed } from 'vue'

const emit = defineEmits(['upgrade'])

const props = withDefaults(
  defineProps<{
    plans: PlansState['plans']
    isLoadingPlanInformation: boolean
    tier: Tier
    paymentSchedule: PaymentSchedule
    upgradeSource: UpgradeSource | null
    isExpanded: boolean
    useTierColor?: boolean
  }>(),
  {
    useTierColor: true,
  },
)

const buttonColorScheme = computed<OzContainedButtonColorScheme>(() => {
  if (!props.useTierColor) {
    return !props.isExpanded ? OzContainedButtonColorScheme.Secondary : OzContainedButtonColorScheme.Primary
  }
  if (props.tier === Tier.Gold) return OzContainedButtonColorScheme.Orange
  return OzContainedButtonColorScheme.Green
})
</script>

<template>
  <div
    :class="[
      'grid items-center',
      !isExpanded && 'grid-cols-payment-schedule-grid gap-x-2 gap-y-0.5',
      isExpanded && 'grid-cols-4',
    ]"
  >
    <div
      class="flex items-center tablet-portrait:flex-col tablet-portrait:items-start order-1 tablet-portrait:col-span-2 grow ps-1 tablet-portrait:ps-0"
    >
      <p class="text-heading-4 font-semibold">
        <template v-if="paymentSchedule === PaymentSchedule.Monthly">{{ __('Pay monthly') }}</template>
        <template v-if="paymentSchedule === PaymentSchedule.Annual">{{ __('Pay annually') }}</template>
      </p>

      <template v-if="tier === Tier.Gold && paymentSchedule === PaymentSchedule.Annual && !isLoadingPlanInformation">
        <OzBadge
          v-if="!isExpanded"
          :color-scheme="OzBadgeColorScheme.Selection"
          class="ms-2"
          :text="__('Save %{discountPercentage}%', { discountPercentage: plans.gold.annual.annual_savings_percentage })"
        ></OzBadge>
        <p v-else class="text-body-small text-grape-500 dark:text-canary-500">
          {{ __('Save %{discountPercentage}%', { discountPercentage: plans.gold.annual.annual_savings_percentage }) }}
        </p>
      </template>

      <template
        v-else-if="tier === Tier.Platinum && paymentSchedule === PaymentSchedule.Annual && !isLoadingPlanInformation"
      >
        <OzBadge
          v-if="!isExpanded"
          :color-scheme="OzBadgeColorScheme.Selection"
          class="ms-2"
          :text="
            __('Save %{discountPercentage}%', { discountPercentage: plans.platinum.annual.annual_savings_percentage })
          "
        ></OzBadge>
        <p v-else class="text-body-small text-grape-500 dark:text-canary-500">
          {{
            __('Save %{discountPercentage}%', { discountPercentage: plans.platinum.annual.annual_savings_percentage })
          }}
        </p>
      </template>
    </div>

    <div
      class="order-3 tablet-portrait:order-2 flex tablet-portrait:flex-col justify-self-start items-baseline tablet-portrait:h-11 tablet-portrait:justify-center ps-1 tablet-portrait:ps-0"
    >
      <div v-if="isLoadingPlanInformation" class="ps-2 tablet-portrait:ps-4">
        <OzLoadingSpinner :size="24" />
      </div>
      <template v-else>
        <template v-if="paymentSchedule === PaymentSchedule.Monthly">
          <p
            class="text-body"
            :data-testid="
              tier === Tier.Gold ? 'proUpgradeDialogGoldMonthlyPrice' : 'proUpgradeDialogPlatinumMonthlyPrice'
            "
          >
            <template v-if="tier === Tier.Gold">{{ plans.gold.monthly.formattedPrice }}</template>
            <template v-if="tier === Tier.Platinum">{{ plans.platinum.monthly.formattedPrice }}</template>
          </p>
        </template>
        <template v-if="paymentSchedule === PaymentSchedule.Annual">
          <p
            class="text-body"
            :data-testid="
              tier === Tier.Gold ? 'proUpgradeDialogGoldAnnualPrice' : 'proUpgradeDialogPlatinumAnnualPrice'
            "
          >
            <template v-if="tier === Tier.Gold">{{ plans.gold.annual.formattedPrice }}</template>
            <template v-if="tier === Tier.Platinum">{{ plans.platinum.annual.formattedPrice }}</template>
          </p>
        </template>
      </template>
    </div>
    <div class="order-2 tablet-portrait:order-3 row-span-2 tablet-portrait:row-span-1 justify-self-end">
      <OzContainedButton
        v-if="!isExpanded"
        key="icon-button"
        :aria-label="__('Continue')"
        :color-scheme="buttonColorScheme"
        :disabled="isLoadingPlanInformation"
        @click="emit('upgrade')"
      >
        <template #icon><OzIcon name="chevron_right_center" :size="16" /></template>
      </OzContainedButton>
      <OzContainedButton
        v-else
        key="text-button"
        data-testid="continuePaymentButton"
        :color-scheme="buttonColorScheme"
        :disabled="isLoadingPlanInformation"
        :text="__('Continue')"
        @click="emit('upgrade')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.grid-cols-payment-schedule-grid {
  grid-template-columns: auto max-content;
}
</style>
